import React, { useState } from 'react';
import { TextField, InputAdornment, IconButton, Typography, FormControlLabel, Checkbox, Box } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CUSTOMER_LEAD_NAME_ALLOWED_CHARS = /[^A-Za-z0-9/& ]/g;

export const CustomTextBox = ({
    value,
    onChange,
    label,
    type = 'ascii_text',
    decimal = false,
    decimalScale = 2,
    casing = 'title',
    error,
    helperText,
    maxLength,
    maxCount,
    defaultValue,
    hideClearIcon = false,
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [emailFormatError, setEmailFormatError] = useState('');
    const [sanitizeWarning, setSanitizeWarning] = useState('');
    const allowDecimal = Boolean(decimal);
    const isControlled = value !== undefined;

    const handleClear = () => {
        if (type === 'email') setEmailFormatError('');
        const clearEvent = { target: { name: props.name, value: '' } };
        onChange(clearEvent);
    };

    const formatValue = (val) => {
        if (!val && val !== 0) return '';
        let processedVal = val.toString().trimStart();

        const skipCasing = ['numbers', 'gst', 'email', 'currency', 'password', 'percentage'].includes(type);

        if (type === 'gst') {
            processedVal = processedVal.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (processedVal.length > 15) processedVal = processedVal.substring(0, 15);
            return processedVal;
        }

        if (type === 'customer_lead_name') {
            processedVal = processedVal.replace(CUSTOMER_LEAD_NAME_ALLOWED_CHARS, '');
        }

        if (type === 'numbers' || type === 'percentage') {
            processedVal = processedVal.replace(/\s/g, '');
            if (allowDecimal) {
                processedVal = processedVal.replace(/[^0-9.]/g, '');
                const parts = processedVal.split('.');
                if (parts.length > 2) processedVal = parts[0] + '.' + parts.slice(1).join('');
                if (parts.length > 1 && parts[1].length > decimalScale) {
                    processedVal = parts[0] + '.' + parts[1].substring(0, decimalScale);
                }
            } else {
                processedVal = processedVal.replace(/\D/g, '');
            }
            if (maxCount && processedVal !== '') {
                const num = parseFloat(processedVal);
                if (num > maxCount) processedVal = maxCount.toString();
            }
        }

        if (maxLength && processedVal.length > maxLength) {
            processedVal = processedVal.substring(0, maxLength);
        }

        if (casing === 'caps') processedVal = processedVal.toUpperCase();
        if (casing === 'title' && !skipCasing) {
            processedVal = processedVal.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
        }

        if (type === 'email') processedVal = processedVal.toLowerCase().trim();

        if (type === 'currency') {
            const raw = processedVal.replace(/,/g, '').replace(/\s/g, '');
            const hasDot = allowDecimal && raw.includes('.');
            const endsWithDot = allowDecimal && raw.endsWith('.');

            if (!allowDecimal) {
                const digits = raw.replace(/\D/g, '');
                return digits ? new Intl.NumberFormat('en-IN').format(digits) : '';
            }

            let normalized = raw.replace(/[^0-9.]/g, '');
            const parts = normalized.split('.');
            if (parts.length > 2) normalized = parts[0] + '.' + parts.slice(1).join('');

            const [intPartRaw, decPartRaw = ''] = normalized.split('.');
            const intDigits = (intPartRaw || '').replace(/\D/g, '');
            const formattedInt = intDigits
                ? new Intl.NumberFormat('en-IN').format(intDigits)
                : '';

            const decDigits = (decPartRaw || '').replace(/\D/g, '').substring(0, decimalScale);
            if (hasDot || endsWithDot) {
                return `${formattedInt}${formattedInt ? '' : '0'}.${decDigits}`;
            }
            return formattedInt;
        }

        return processedVal;
    };

    const handleChange = (e) => {
        const rawValue = e.target.value;
        const displayValue = formatValue(rawValue);

        if (type === 'email') {
            if (!displayValue || EMAIL_REGEX.test(displayValue)) {
                setEmailFormatError('');
            }
        }

        if (type === 'customer_lead_name') {
            const hadInvalidChars = CUSTOMER_LEAD_NAME_ALLOWED_CHARS.test(rawValue);
            setSanitizeWarning(
                hadInvalidChars
                    ? 'Only letters, numbers, spaces, &, and / are allowed.'
                    : ''
            );
        } else if (sanitizeWarning) {
            setSanitizeWarning('');
        }

        const valueToSend = type === 'currency'
            ? displayValue.replace(/,/g, '')
            : displayValue;

        onChange({
            ...e,
            target: { ...e.target, name: props.name, value: valueToSend }
        });
    };

    const handleBlur = (e) => {
        let val = e.target.value;
        const cleanVal = type === 'currency' ? val.replace(/,/g, '') : val;
        const trimmed = cleanVal.trim();

        if (type === 'email') {
            setEmailFormatError(trimmed && !EMAIL_REGEX.test(trimmed) ? 'Enter a valid email address' : '');
        }

        if (trimmed !== cleanVal) {
            onChange({ ...e, target: { ...e.target, name: props.name, value: trimmed } });
        }
        if (props.onBlur) props.onBlur(e);
    };

    const getInputValue = () => {
        const val = isControlled ? (value || '') : (defaultValue || '');
        if (type === 'currency') return formatValue(val);
        return val;
    };

    const getInputType = () => {
        if (type === 'password') return showPassword ? 'text' : 'password';
        if (type === 'email') return 'email';
        if (type === 'numbers' || type === 'currency') return allowDecimal ? 'text' : 'tel';
        return 'text';
    };

    const getInputMode = () => {
        if (type === 'currency' || type === 'numbers') {
            return allowDecimal ? 'decimal' : 'numeric';
        }
        return props.inputProps?.inputMode;
    };

    const currentLength = (value || "").toString().length;
    const isMaxReached = type === 'numbers' && maxCount && parseInt(value) >= maxCount;
    const effectiveError = error || emailFormatError;
    const inputValue = getInputValue();
    // Outlined label must stay shrunk for semantic password fields: browser autofill can fill the DOM
    // without updating React value, and native password inputs can fail to drive MUI's "filled" state.
    const shouldShrinkOutlinedLabel = type === "password" || Boolean(inputValue);

    const autoHelperText = emailFormatError
        ? emailFormatError
        : error
            ? (helperText || error)
            : sanitizeWarning
                ? sanitizeWarning
                : helperText
                    ? helperText
                    : maxLength
                        ? `${maxLength - currentLength} characters remaining`
                        : (type === 'numbers' && maxCount)
                            ? (isMaxReached ? `Maximum reached (${maxCount})` : `Maximum is ${maxCount}`)
                            : null;

    const startAdornment = props.InputProps?.startAdornment;

    const textField = (
        <TextField
            {...props}
            fullWidth
            label={label}
            InputLabelProps={{
                ...props.InputLabelProps,
                ...(shouldShrinkOutlinedLabel ? { shrink: true } : {}),
            }}
            type={getInputType()}
            value={getInputValue()}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(effectiveError)}
            helperText={autoHelperText}
            inputProps={{
                inputMode: getInputMode(),
                maxLength,
                ...props.inputProps,
            }}
            InputProps={{
                ...props.InputProps,
                ...(type === "password" ? { notched: true } : {}),
                startAdornment: startAdornment,
                endAdornment: (
                    <InputAdornment position="end">
                        {type === 'percentage' && (
                            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, mr: 1 }}>
                                %
                            </Typography>
                        )}
                        {!hideClearIcon && (value || defaultValue) && (
                            <IconButton onClick={handleClear} edge="end" size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
            sx={{
                "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                    },
                    "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px transparent inset",
                    WebkitTextFillColor: "inherit",
                    transition: "background-color 5000s ease-in-out 0s",
                    },
                },
                "& .MuiFormHelperText-root": {
                    textAlign: "right",
                    marginRight: 0,
                },
                ...props.sx
            }}
        />
    );

    if (type === 'password') {
        return (
            <Box sx={{ width: '100%' }}>
                {textField}
                <FormControlLabel
        control={
            <Checkbox
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                size="small"
            />
        }
        label="Show password"
        sx={{
            mt: -0.9,
            ml: 0,
            userSelect: "none",
            "& .MuiFormControlLabel-label": {
                fontSize: "0.84rem",
                color: "grey.500",
            },
        }}
    />
            </Box>
        );
    }

    return textField;
};